<template>
  <slide-out
    dock="right"
    :size="600"
    :min-size="500"
    id="create-register-slideout"
    ref="registerSlideout"
    :allow-resize="true"
    :append-to="'#app'"
    :visible.sync="slideoutVisible"
    @close="closeSlideout">
    <template v-slot:header>
      <h3 class="font-weight-bold pa-1 sideOutHeaderTitle">
        {{ isEditing ? 'Edit Register' : 'Create New Register' }}
      </h3>
      <v-btn class="font-weight-bold" color="mid-grey2" large @click="closeSlideout" icon>
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </template>

    <div style="height: 100%">
      <v-form
        ref="registerForm"
        v-model="formValid"
        class="h-100 pa-4"
        style="overflow-y: auto">
        <v-card outlined class="pa-4 rounded-lg mb-4">
          <div class="d-flex align-center mb-4">
            <v-icon color="primary" class="mr-2">mdi-information</v-icon>
            <span class="subtitle-1 font-weight-bold">Register Details</span>
          </div>
          <v-text-field
            v-model="formData.name"
            label="Register Name"
            placeholder="Enter register name"
            outlined
            dense
            :rules="[rules.required]"
            :disabled="saving">
          </v-text-field>
          <v-row>
            <v-col cols="12" md="6">
              <v-select
                attach
                v-model="formData.type"
                :items="registerTypes"
                label="Register Type"
                outlined
                dense
                :disabled="saving">
              </v-select>
            </v-col>
            <v-col cols="12" md="6">
              <v-select
                attach
                v-model="formData.status"
                :items="statusOptions"
                label="Status"
                outlined
                dense
                :rules="[rules.required]"
                :disabled="saving">
              </v-select>
            </v-col>
          </v-row>
          <v-textarea
            v-model="formData.description"
            label="Description"
            placeholder="Optional description"
            outlined
            dense
            rows="3"
            :disabled="saving">
          </v-textarea>
        </v-card>
        
        <v-card outlined class="pa-4 rounded-lg mb-4">
          <div class="d-flex align-center mb-4">
            <v-icon color="primary" class="mr-2">mdi-domain</v-icon>
            <span class="subtitle-1 font-weight-bold">Facility Assignment</span>
          </div>
          <v-radio-group v-model="facilitySelectionType" row class="mb-3">
            <v-radio label="Single Facility" value="facility"></v-radio>
            <v-radio label="Facility Group" value="group"></v-radio>
          </v-radio-group>
          <v-autocomplete
            attach
            v-model="formData.facilityId"
            :items="facilities"
            label="Select Facility"
            placeholder="Choose facility for this register"
            outlined
            dense
            clearable
            hide-details
            v-if="facilitySelectionType === 'facility'"
            :disabled="saving || facilitySelectionType !== 'facility'"
            item-text="name"
            item-value="id">
            <template v-slot:selection="{ item }">
              <div class="d-flex align-center">
                <v-icon small class="mr-2" color="primary">mdi-factory</v-icon>
                <span>{{ item.name }}</span>
              </div>
            </template>
            <template v-slot:item="{ item }">
              <div class="d-flex align-center py-2">
                <v-icon small class="mr-3" color="primary">mdi-factory</v-icon>
                <div>
                  <div class="subtitle-2">{{ item.name }}</div>
                  <div class="caption grey--text" v-if="item.description">
                    {{ item.description }}
                  </div>
                </div>
              </div>
            </template>
          </v-autocomplete>
          <v-autocomplete
            attach
            class="mt-3"
            v-model="formData.facilityGroupId"
            :items="groups"
            label="Select Facility Group"
            placeholder="Choose facility group for this register"
            outlined
            dense
            clearable
            hide-details
            v-if="facilitySelectionType === 'group'"
            :disabled="saving || facilitySelectionType !== 'group'"
            item-text="name"
            item-value="id">
            <template v-slot:selection="{ item }">
              <div class="d-flex align-center">
                <v-icon small class="mr-2" color="orange">mdi-group</v-icon>
                <span>{{ item.name }}</span>
                <v-chip x-small color="info" class="ml-2">Facility Group</v-chip>
              </div>
            </template>
            <template v-slot:item="{ item }">
              <div class="d-flex align-center py-2">
                <v-icon small class="mr-3" color="orange">mdi-group</v-icon>
                <div>
                  <div class="subtitle-2">
                    {{ item.name }}
                    <v-chip x-small color="info" class="ml-2">Facility Group</v-chip>
                  </div>
                  <div class="caption grey--text" v-if="item.description">
                    {{ item.description }}
                  </div>
                </div>
              </div>
            </template>
          </v-autocomplete>
          <v-alert
            v-if="facilitySelectionType === 'group' && formData.facilityGroupId"
            outlined
            type="info"
            dense
            class="mt-3">
            <div class="text-caption">
              <strong>Facility Group imports:</strong>
              <ul class="mt-1 mb-0">
                <li>Choose Shared or Separated during import.</li>
                <li>Shared: every member Facility sees all rows.</li>
                <li>Separated: each Facility sees only its assigned rows.</li>
              </ul>
            </div>
          </v-alert>
        </v-card>

        <v-card outlined class="pa-4 rounded-lg">
          <div class="d-flex align-center mb-4">
            <v-icon color="info" class="mr-2">mdi-account-multiple</v-icon>
            <span class="subtitle-1 font-weight-bold">User Assignment</span>
            <v-chip small color="primary" outlined class="ml-2">
              {{ selectedUsers.length }}
            </v-chip>
          </div>
          <div class="mb-4">
            <UserSelector
              :hide-details="true"
              v-model="selectedUsers"
              label="Search and assign users"
              multiple>
            </UserSelector>
          </div>
          <v-alert outlined type="info" dense>
            <div class="caption">
              <v-icon small class="mr-1">mdi-information</v-icon>
              You will be automatically assigned to this register as the creator.
            </div>
          </v-alert>
        </v-card>
      </v-form>

    </div>
    <template v-slot:footer>
      <!-- Action Buttons - Fixed at bottom -->
      <v-card-actions class="px-4 py-3">
        <v-btn @click="closeSlideout" :disabled="saving"> Cancel </v-btn>
        <v-spacer></v-spacer>
        <v-btn color="primary" @click="saveRegister" :loading="saving" :disabled="!isFormValid">
          <v-icon left>{{ isEditing ? 'mdi-content-save' : 'mdi-plus' }}</v-icon>
          {{ isEditing ? 'Update Register' : 'Create Register' }}
        </v-btn>
      </v-card-actions>
    </template>
  </slide-out>
</template>

<script>
  import { mapActions, mapGetters } from 'vuex';
  import formRules from '@Share/config/form-rules';
  import UserSelector from '@Share/components/UserSelector.vue';
  import {
    RegisterType,
    RegisterStatus,
    RegisterTypeOptions,
    RegisterStatusOptions,
  } from '../../config/enums.js';
  import { log, warn, error } from '@/utils/debugLogger';
  import facilityService from '@Facility/service/facility.service';
  export default {
    name: 'CreateRegister',
    components: {
      UserSelector,
    },
    props: {
      value: {
        type: Boolean,
        default: false,
      },
      register: {
        type: Object,
        default: null,
      },
    },
    data() {
      return {
        slideoutVisible: false,
        
        formValid: false,
        saving: false,
        facilitySelectionType: 'facility', // 'facility' or 'group'
        facilities: [],
        groups: [],
        selectedUsers: [],
        formData: {
          name: '',
          type: null,
          status: RegisterStatus.Draft,
          description: '',
          facilityId: null,
          facilityGroupId: null,
          
        },
        rules: {
          required: formRules.required,
        },
        registerTypes: RegisterTypeOptions,
        statusOptions: RegisterStatusOptions,
        
      };
    },
    computed: {
      ...mapGetters('ACCOUNT', {
        companyId: 'GET_COMPANY_ID',
        currentUser: 'GET_USER',
      }),

      isEditing() {
        return !!this.register?.id;
      },

      isFormValid() {
        return this.formValid;
      },

      
    },
    methods: {
      ...mapActions('REGISTERS', {
        createRegister: 'CREATE_REGISTER',
        updateRegister: 'UPDATE_REGISTER',
      }),

      async fetchFacilities() {
        try {
          const res = await facilityService.get({ itemsPerPage: -1 });
          // API returns Page<FacilityVM> with { items: [...], total: n }
          // Axios wraps it in res.data, so access the inner items array
          this.facilities = res.data?.items || [];
        } catch (err) {
          error('CreateRegister', 'Failed to fetch facilities:', err);
          this.facilities = [];
        }
      },

      async fetchGroups() {
        return this.$store.dispatch('GROUPS/GET_GROUPS').then((data) => {
          this.groups = data;
        });
      },

      async initialize() {
        try {
          await Promise.all([this.fetchFacilities(), this.fetchGroups()]);

          // Initialize form data based on mode
          if (this.isEditing) {
            this.initializeEditMode();
          } else {
            // Auto-assign current user for create mode
            if (this.currentUser && this.currentUser.id) {
              this.selectedUsers = [this.currentUser.id];
            }
          }
        } catch (err) {
          error('CreateRegister', 'Failed to initialize register form', err);
          this.$dialog.notify.error('Failed to load data');
        }
      },

      initializeEditMode() {
        if (!this.register) return;

        // Map register data to form data
        this.formData = {
          name: this.register.name || '',
          type: this.normalizeType(this.register.type),
          status: this.normalizeStatus(this.register.status),
          description: this.register.description || '',
          facilityId: this.register.facilityId || null,
          facilityGroupId: this.register.facilityGroupId || null,
          
        };

        // Set facility selection type based on data structure
        this.facilitySelectionType = this.register.facilityGroupId ? 'group' : 'facility';

        // Set assigned users
        // Check if assignedUsers is array of objects extract ids
        if (this.register.assignedUsers && this.register.assignedUsers.length > 0 && typeof this.register.assignedUsers[0] === 'object') {
          this.selectedUsers = this.register.assignedUsers.map((user) => user.id);
        } else {
          this.selectedUsers = this.register.assignedUsers || [];
        }
      },

      

      async saveRegister() {
        if (!this.isFormValid) {
          this.$dialog.notify.error('Please complete the required fields');
          return;
        }

        this.saving = true;

        try {
          const registerData = {
            ...this.formData,
            assignedUsers: this.selectedUsers,
            facilityGroupId:
              this.facilitySelectionType === 'group' ? this.formData.facilityGroupId : null,
            facilityId: this.facilitySelectionType === 'facility' ? this.formData.facilityId : null,
          };

          if (this.isEditing) {
            await this.updateRegister({
              id: this.register.id,
              data: registerData,
            });
            this.$dialog.notify.success('Register updated successfully');
          } else {
            await this.createRegister(registerData);
            this.$dialog.notify.success('Register created successfully');
          }

          // Close slideout and emit save event (with the new register's name so the list can show it)
          this.closeSlideout();
          this.$emit('save', { created: !this.isEditing, name: registerData.name });
        } catch (err) {
          error('CreateRegister', `${this.isEditing ? 'Update' : 'Create'} register failed`, err);
          this.$dialog.notify.error(
            `Failed to ${this.isEditing ? 'update' : 'create'} register: ${err.message}`,
          );
        } finally {
          this.saving = false;
        }
      },

      normalizeType(value) {
        if (typeof value === 'number') {
          return RegisterTypeOptions.some((option) => option.value === value) ? value : null;
        }
        if (!value) return null;
        const map = {
          equipment: RegisterType.Equipment,
          pipeline: RegisterType.Pipeline,
          structural: RegisterType.Structural,
          inspection: RegisterType.Inspection,
          general: RegisterType.General,
          annotation: RegisterType.General,
          anomaly: RegisterType.Anomaly,
          avainspect: RegisterType.AvaInspect,
          'ava inspect': RegisterType.AvaInspect,
          'ava inspection': RegisterType.AvaInspect,
          scaffold: RegisterType.Scaffold,
          permit: RegisterType.Permit,
          container: RegisterType.Container,
          operation: RegisterType.Operation,
        };
        const key = String(value).trim().toLowerCase();
        if (/^\d+$/.test(key)) {
          const numeric = Number(key);
          return RegisterTypeOptions.some((option) => option.value === numeric) ? numeric : null;
        }
        return map[key] || null;
      },

      normalizeStatus(value) {
        if (typeof value === 'number') return value;
        if (!value) return RegisterStatus.Draft;
        const map = {
          active: RegisterStatus.Active,
          draft: RegisterStatus.Draft,
          inactive: RegisterStatus.Inactive,
        };
        const key = String(value).toLowerCase();
        return map[key] || RegisterStatus.Draft;
      },

      closeSlideout() {
        this.slideoutVisible = false;
        this.$emit('input', false);

        // Reset form after slideout closes
        setTimeout(() => {
          this.resetForm();
          // Clear any previous register reference when dialog closes
          if (this.$listeners && this.$listeners['update:register']) {
            this.$emit('update:register', null);
          }
        }, 300);
      },

      resetForm() {
        // Always reset to defaults to avoid leaking edit state into create mode
        this.formData = {
          name: '',
          type: null,
          status: (this.$options?.components && this.$options.components) ? undefined : undefined,
          description: '',
          facilityId: null,
          facilityGroupId: null,
          
        };
        // Ensure status defaults correctly (numeric enum if available)
        try {
          const { RegisterStatus } = require('../../config/enums.js');
          this.formData.status = RegisterStatus.Draft;
        } catch (e) {
          this.formData.status = 'Draft';
        }
        this.selectedUsers = this.currentUser ? [this.currentUser.id] : [];
        this.facilitySelectionType = 'facility';

        this.userSearch = null;

        // Reset form validation
        this.$nextTick(() => {
          this.$refs.registerForm?.resetValidation();
        });
      },

      open() {
        this.slideoutVisible = true;
        this.initialize();
      },
    },

    watch: {
      value: {
        immediate: true,
        handler(newValue) {
          this.slideoutVisible = newValue;
          if (newValue) {
            this.initialize();
          }
        },
      },

      register: {
        handler(newRegister) {
          // Reinitialize when register prop changes (for edit mode)
          if (this.slideoutVisible && newRegister) {
            this.initializeEditMode();
          }
        },
        deep: true,
      },

      slideoutVisible(newValue) {
        this.$emit('input', newValue);
      },

      facilitySelectionType() {
        // Keep selections mutually exclusive
        if (this.facilitySelectionType === 'facility') {
          this.formData.facilityGroupId = null;
        } else if (this.facilitySelectionType === 'group') {
          this.formData.facilityId = null;
        }
      },
    },

    async created() {
      if (this.slideoutVisible) {
        await this.initialize();
      }
    },
  };
</script>

<style lang="scss">
  #create-register-slideout {
    :deep(.slide-out-header) {
      border-bottom: 1px solid #e0e0e0;
    }

    :deep(.v-input--radio-group .v-radio) {
      margin-right: 24px;
    }

    .selected-users-list {
      border: 1px solid #e0e0e0;
      border-radius: 4px;
      padding: 8px;
      background-color: #f9f9f9;
    }

    .sideOutHeaderTitle {
      flex-grow: 1;
    }

    
  }
</style>
